import QueryString from "query-string";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import wembiiLogoWhite from "../../assets/wembii-logo-white.png";
import wembiiLogo from "../../assets/wembii-logo.png";
import { useGlobalCore } from "../../cores/globals";
import { useRouterCore } from "../../cores/router";
import { useSessionCore } from "../../cores/session";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { LocalSelector } from "../component/LocaleSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import useVisibility from "../../hooks/useVisibility/useVisibility.hook";
import { useAuthPageTheme } from "../../hooks/useAuthPageTheme/useAuthPageTheme";
import { GoogleSignInButton } from "../component/GoogleSignInButton";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export const Login = () => {
  const intl = useIntl();
  const { search } = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { toggleVisibility, visible } = useVisibility({ initValue: false });
  const { theme, toggleTheme } = useAuthPageTheme();

  const { postSession, postSessionWithGoogle, logout } = useSessionCore();
  const { routes } = useRouterCore();
  const { reset } = useGlobalCore();

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const emailInput = form.querySelector<HTMLInputElement>('[name="email"]');
    const passwordInput = form.querySelector<HTMLInputElement>(
      '[name="password"]'
    );
    const emailVal = (emailInput?.value ?? email).trim();
    const passwordVal = passwordInput?.value ?? password;
    if (!emailVal || !passwordVal) {
      toast.error(intl.formatMessage({ id: "app.auth.fillCredentials" }));
      return;
    }
    postSession(emailVal, passwordVal);
  };

  useEffect(() => {
    const { emailConfirmed } = QueryString.parse(search);
    if (emailConfirmed) {
      reset();
      logout();
    }
  }, [logout, reset, search]);

  const isLight = theme === "light";

  return (
    <div
      id="login"
      className={`login-v2${isLight ? " login-v2--light" : ""}`}
    >
      <div className="login-v2__hero" aria-hidden="true">
        <div className="login-v2__hero-bg" />
      </div>
      <div className="login-v2__panel">
        <div className="login-v2__panel-inner">
          <div className="login-v2__top-row">
            <div className="login-v2__brand">
              <img
                src={isLight ? wembiiLogo : wembiiLogoWhite}
                alt="Wembii"
                className="login-v2__logo"
              />
            </div>
            <button
              type="button"
              className="login-v2__theme-toggle"
              onClick={toggleTheme}
              aria-label={
                isLight
                  ? intl.formatMessage({ id: "app.authTheme.switchToDark" })
                  : intl.formatMessage({ id: "app.authTheme.switchToLight" })
              }
              title={
                isLight
                  ? intl.formatMessage({ id: "app.authTheme.switchToDark" })
                  : intl.formatMessage({ id: "app.authTheme.switchToLight" })
              }
            >
              <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
            </button>
          </div>

          <div className="login-v2__form-wrap">
            <h1 className="login-v2__title">
              {intl.formatMessage({ id: "app.LogInToYourAccount" })}
            </h1>
            <p className="login-v2__subtitle">
              {intl.formatMessage({ id: "app.YouDoNotHaveAnAccount" })}{" "}
              <Link to={routes.createAccount} className="login-v2__link">
                {intl.formatMessage({ id: "app.checkIn" })}
              </Link>
            </p>

            <Form
              onSubmit={onFormSubmit}
              className="login-v2__form"
              noValidate
            >
              <FormGroup className="login-v2__field">
                <Label for="login-email" className="login-v2__label">
                  {intl.formatMessage({ id: "app.emailTxt" })}
                </Label>
                <Input
                  type="email"
                  name="email"
                  id="login-email"
                  placeholder={intl.formatMessage({ id: "app.emailTxt" })}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-v2__input"
                  autoComplete="email"
                />
              </FormGroup>

              <FormGroup className="login-v2__field">
                <Label for="login-password" className="login-v2__label">
                  {intl.formatMessage({ id: "app.passwordTxt" })}
                </Label>
                <div className="login-v2__password-wrap">
                  <Input
                    type={visible ? "text" : "password"}
                    name="password"
                    id="login-password"
                    placeholder={intl.formatMessage({ id: "app.passwordTxt" })}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-v2__input login-v2__input--password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={toggleVisibility}
                    className="login-v2__toggle-password"
                    aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                  </button>
                </div>
                <Link
                  to={routes.forgotPassword}
                  className="login-v2__forgot"
                >
                  {intl.formatMessage({ id: "app.forgotPassword" })}
                </Link>
              </FormGroup>

              <Button type="submit" className="login-v2__submit">
                {intl.formatMessage({ id: "app.loginButton" })}
              </Button>

              {googleClientId ? (
                <>
                  <p className="login-v2__google-divider" role="presentation">
                    {intl.formatMessage({ id: "app.auth.signInDivider" })}
                  </p>
                  <GoogleSignInButton
                    clientId={googleClientId}
                    isLight={isLight}
                    locale={intl.locale}
                    onCredential={(credential) => {
                      void postSessionWithGoogle(credential);
                    }}
                  />
                </>
              ) : null}

              <div
                className={`login-v2__locale ${
                  isLight ? "login-v2__locale--light" : "login-v2__locale--dark"
                }`}
              >
                <LocalSelector />
              </div>
            </Form>
          </div>

          <div className="login-v2__footer">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.wembii.com/terminos-y-condiciones/"
              className="login-v2__legal"
            >
              {intl.formatMessage({ id: "app.terms" })}
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.wembii.com/privacy-policy/"
              className="login-v2__legal"
            >
              {intl.formatMessage({ id: "app.privacyPolicy" })}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
